import { z } from 'zod';
import { requirementPatternEnum, hex64Schema, idSchema, isoDateSchema, isoDateTimeSchema, } from './entities.js';
/**
 * Training-checklist DTO — the heart of the volunteer's `/me` dashboard
 * (PRD 06 §`/me` — the volunteer dashboard, item 3 "Training checklist").
 *
 * Each row corresponds to a `compliance_requirements` row that applies to
 * the signed-in volunteer (resolved by `resolveApplicableRequirements(person)`).
 * The shape here is a presentation-layer projection: the API computes the
 * badge + the "what to do next" block + the upload-affordance flag; the
 * web client just renders it. Keeping the projection on the server side
 * means the rules ("you can re-upload while submitted, but not after
 * verified") live in one place and the M12b UI can't drift.
 *
 * The `/me` GET response embeds an array of these rows; see
 * `meDashboardResponseSchema` in `api.ts`.
 */
// ---------------------------------------------------------------------------
// Status badge — derived from the underlying compliance_records row.
// ---------------------------------------------------------------------------
/**
 * Volunteer-facing status the row renders. Not the same as
 * `compliance_record_status` (PRD 03 §3): the underlying enum has
 * pending / submitted / verified / expiring_soon / expired /
 * archived / superseded — but the dashboard collapses several of those
 * into the badges PRD 06 §`/me` calls out.
 *
 *   - `pending` — no record yet OR record is `pending` (volunteer needs
 *     to act); the row's affordance is the upload (or ack) action.
 *   - `awaiting_verification` — a `submitted` record (volunteer uploaded;
 *     waiting on admin). Re-upload allowed; PRD 06 §Upload flow step 5.
 *   - `verified` — `verified` AND `expiry_date` further away than the
 *     item type's first reminder offset (or null for ack rows).
 *   - `expiring_soon` — `verified` AND within the first reminder offset
 *     of expiry. Render with amber hint.
 *   - `expired` — `expired` row; render with red hint.
 *   - `inactive` — `archived` / `superseded` — read-only marker shown so
 *     the volunteer knows the item is no longer actionable from this
 *     surface.
 */
export const trainingChecklistStatusEnum = z.enum([
    'pending',
    'awaiting_verification',
    'verified',
    'expiring_soon',
    'expired',
    'inactive',
]);
/**
 * The primary action the row offers. Drives the inline-upload widget
 * (PRD 06 §Upload flow) and surfaces a hint pointing at the inline
 * acknowledgement pane on `/me` for sign-pattern rows.
 *
 *   - `upload_evidence` — opens the inline upload widget against the
 *     pre-signed PUT URL flow (`POST /me/evidence` → object PUT →
 *     `POST /me/evidence/confirm`). Maps to `requirement_pattern='upload'`.
 *   - `visit_acknowledgement` — for `requirement_pattern='sign'` rows.
 *     The volunteer signs the policy inline on `/me` via the Pending
 *     acknowledgements pane (`POST /me/ack/start` → `step-up` →
 *     `confirm`). M13 retired the per-delivery `/ack/:token` page; this
 *     affordance kind now drives copy that points the volunteer at the
 *     Pending acknowledgements pane on the same dashboard.
 *   - `none` — verified-and-fresh, inactive, or `admin_attestation`
 *     pattern (which never surfaces an action on `/me`).
 */
export const trainingChecklistAffordanceKindEnum = z.enum([
    'upload_evidence',
    'visit_acknowledgement',
    'none',
]);
// ---------------------------------------------------------------------------
// Sub-shapes for the "what to do next" block.
// ---------------------------------------------------------------------------
/**
 * One numbered step in the row's instruction block. PRD 06 §85.3 calls
 * for terse three-step lists ("1. Register at Geccko… 2. Complete Course
 * 1, then Course 2… 3. Download the PDF certificate…").
 */
export const trainingChecklistInstructionStepSchema = z.object({
    step_number: z.number().int().positive(),
    text: z.string().min(1),
});
/**
 * External link rendered inline in the instruction block — Geccko course
 * landing page, OCG portal, etc. The label is what the volunteer reads;
 * the URL is what they click.
 */
export const trainingChecklistExternalLinkSchema = z.object({
    label: z.string().min(1),
    url: z.string().url(),
});
/**
 * The "what to do next" block (PRD 06 §85.3). Null when there is nothing
 * for the volunteer to do — e.g. a verified-and-fresh row, or an inactive
 * row.
 *
 * `primary_action_label` mirrors the affordance kind: "Upload certificate"
 * / "Upload screenshot" for the upload path, "Open acknowledgement" for
 * the typed-name path.
 */
export const trainingChecklistWhatToDoSchema = z.object({
    external_link: trainingChecklistExternalLinkSchema.nullable(),
    instructions: z.array(trainingChecklistInstructionStepSchema),
    typical_time: z.string().nullable(),
    primary_action_label: z.string().min(1),
});
/**
 * Verified-state details shown beneath the row (PRD 06 §85, "For verified
 * items: the verified date, expiry date, the admin who verified, and a
 * download link"). Populated only when the row's status reflects a
 * current verification (`verified` / `expiring_soon` / `expired`).
 *
 * `evidence_download_url` is a short-expiry pre-signed GET; it's null on
 * acknowledgement rows (the receipt PDF lives in the acknowledgement
 * history section instead) and on rows that were verified before the
 * volunteer-self-upload flow existed.
 */
export const trainingChecklistVerifiedDetailsSchema = z.object({
    verified_on: isoDateTimeSchema,
    expiry_date: isoDateSchema.nullable(),
    verified_by_display: z.string().min(1).nullable(),
    evidence_download_url: z.string().url().nullable(),
});
// ---------------------------------------------------------------------------
// The row.
// ---------------------------------------------------------------------------
/**
 * One training-checklist row. Strict cardinality:
 *   - `what_to_do_next` is null exactly when `affordance_kind === 'none'`.
 *   - `verified_details` is non-null exactly when `status` is one of
 *     `verified` / `expiring_soon` / `expired`.
 *   - `current_record_id` is null exactly when no `compliance_records`
 *     row exists yet for this (person, item_type) — the "first time
 *     a volunteer is told to do Foundation training" state.
 *
 * The status_label is the human-readable string the badge renders
 * ("Verified — expires 14 Jun 2028" / "Expiring in 9 days" /
 * "Awaiting verification") so the web layer never has to format
 * dates or pluralise day-counts; the API does it once and the same
 * copy ships to every device.
 */
export const trainingChecklistRowSchema = z.object({
    requirement_id: idSchema,
    short_code: z.string().min(1),
    name: z.string().min(1),
    short_description: z.string().min(1),
    pattern: requirementPatternEnum,
    status: trainingChecklistStatusEnum,
    status_label: z.string().min(1),
    affordance_kind: trainingChecklistAffordanceKindEnum,
    what_to_do_next: trainingChecklistWhatToDoSchema.nullable(),
    verified_details: trainingChecklistVerifiedDetailsSchema.nullable(),
    current_record_id: idSchema.nullable(),
    /** The evidence currently linked to this record, if any. The SPA
     *  surfaces it as "Your file: <filename>" with a presigned download
     *  URL so the volunteer can confirm what they uploaded. Null when
     *  no upload has happened yet on this record. */
    current_evidence: z
        .object({
        filename: z.string(),
        uploaded_at: isoDateTimeSchema,
        download_url: z.string().url(),
        download_url_expires_at: isoDateTimeSchema,
    })
        .nullable(),
});
// ---------------------------------------------------------------------------
// Acknowledgement history entry — surfaced on `/me` per PRD 06 §85.4.
// ---------------------------------------------------------------------------
/**
 * One row in the "Acknowledgement history" section of `/me`. Sourced
 * from `acknowledgement_signatures` joined to `documents`.
 */
export const meAcknowledgementHistoryEntrySchema = z.object({
    signature_id: idSchema,
    document_id: idSchema,
    document_title: z.string().min(1),
    version: z.string().min(1),
    submitted_at_utc: isoDateTimeSchema,
    acknowledgement_text_version: z.string().min(1),
    document_sha256: hex64Schema,
    receipt_download_url: z.string().url().nullable(),
});
// ---------------------------------------------------------------------------
// Reminder history entry — surfaced on `/me` per PRD 06 §85.5.
// ---------------------------------------------------------------------------
/**
 * One row in the "Reminder history" (collapsed by default) section of
 * `/me`. Capped server-side at the last 12 months. Sourced from the
 * `reminders` table joined to the item type for friendly labels.
 */
export const meReminderHistoryEntrySchema = z.object({
    reminder_id: idSchema,
    record_id: idSchema,
    requirement_short_code: z.string().min(1),
    requirement_name: z.string().min(1),
    template: z.string().min(1),
    sent_at: isoDateTimeSchema,
    /** `failed` / `bounced` rows render a "we couldn't reach you" hint. */
    delivery_status: z.enum(['sent', 'bounced', 'failed']),
});
//# sourceMappingURL=training-checklist.js.map